import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import { Box, styled, useTheme, Typography, Dialog, Button } from '@mui/material'
import { socket, recordingSocket } from '../../../config/SocketApi';
import Peer from 'simple-peer';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../auth/hooks/useAuthContext';
import StudentMeeting from './StudentMeeting';
import { useNavigate } from 'react-router-dom';
import { getIceConfig } from '../../../config/iceConfig';
import { SOCKET_EVENTS } from '../../../config/socketEvents';
import { emitReadinessWithRetry, safeSignalPeer } from '../helpers/realtimeUtils';

const blobToBase64 = async (blob) => {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const chunkSize = 0x8000;
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
    }
    return window.btoa(binary);
};

// STYLED COMPONENTS - MOVED OUTSIDE to prevent recreation on every render
const AskedForMicWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.text.secondary}`,
    borderRadius: '16px',
    margin: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    padding: 16,
    maxWidth: '85vw',
    width: '280px',
    '@media (max-width: 768px)': {
        padding: 12,
        width: '250px',
        '& .MuiTypography-h4': {
            fontSize: '1rem',
            textAlign: 'center',
        },
    },
}));

const ButtonsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column-reverse', // Accept button on top
    width: '100%',
    margin: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    backgroundColor: theme.palette.background.default,
    gap: 10,
    '& .MuiButton-root': {
        width: '100%',
    },
}));

const VideoBox = styled(Box)(({ showCamera, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    flex: showCamera ? 6 : undefined,
    padding: showCamera ? '8px' : 0,
    '@media (max-width: 768px)': {
        flex: showCamera ? 1 : undefined,
        padding: showCamera ? '4px' : 0,
        minWidth: showCamera ? '150px' : undefined,
    },
}));

const StudentVideoStream = ({ isMobileOverlay = false, micRequestOpen = false, onMicRequestHandled }) => {
    const navigate = useNavigate();
    const [tutorStream, setTutorStream] = useState();
    const [directTutorAudioStream, setDirectTutorAudioStream] = useState();

    const [attendees, setAttendees] = useState([])
    const [meetingStarted, setMeetingStarted] = useState(false)
    const [tutorCamEnabled, setTutorCamEnabled] = useState(false)
    const askedForMic = micRequestOpen;
    const [audioPlaybackBlocked, setAudioPlaybackBlocked] = useState(false)
    const theme = useTheme();
    const { user } = useAuthContext() || JSON.parse(localStorage.getItem("user"));
    const { t } = useTranslation();
    
    // Log component mount for debugging
    useEffect(() => {
        console.log("[StudentVideoStream] *** COMPONENT MOUNTED ***");
        console.log("[StudentVideoStream] Socket connected:", socket.connected);
        console.log("[StudentVideoStream] Socket ID:", socket.id);
        console.log("[StudentVideoStream] User:", user?.id, user?.full_name);
        
        return () => {
            console.log("[StudentVideoStream] *** COMPONENT UNMOUNTING ***");
        };
    }, []);
    
    // Check if the stream has video tracks
    const hasVideoTrack = tutorStream && tutorStream.getVideoTracks().length > 0;
    
    // Show camera box only when:
    // 1. We have a stream WITH video tracks
    // 2. Tutor's camera is enabled (via socket event)
    // 3. Not in meeting mode
    const showCamera = hasVideoTrack && tutorCamEnabled && !meetingStarted;
    const hasTutorAudio = Boolean(
        tutorStream?.getAudioTracks?.().length || directTutorAudioStream?.getAudioTracks?.().length
    );
    
    // Log stream info for debugging
    useEffect(() => {
        if (tutorStream) {
            console.log("[StudentVideoStream] Stream updated:", {
                streamId: tutorStream.id,
                active: tutorStream.active,
                videoTracks: tutorStream.getVideoTracks().length,
                audioTracks: tutorStream.getAudioTracks().length,
                tutorCamEnabled,
                showCamera
            });
        }
    }, [tutorStream, tutorCamEnabled, showCamera]);
    
    const studentVideoRef = useRef()
    const tutorAudioRef = useRef()
    const directTutorAudioRef = useRef()
    const camerBoxRef = useRef()
    const studentPeerRef = useRef();
    const directTutorAudioPeerRef = useRef();
    const directTutorMediaStreamRef = useRef();
    const directTutorAudioStreamRef = useRef();
    const senderStudentsPeerRefs = useRef([]);
    const studentsPeerRefs = useRef([])
    const attendeesRef = useRef([])
    const serverHandlerPeer = useRef()
    const directTutorMeetingPeerRef = useRef(null)
    const isReceivingCameraStream = useRef(false); // Track if we're already receiving camera stream
    const cancelVideoReadinessRef = useRef();
    const studentAudioRecorderRef = useRef(null);

    const stopStudentAudioRecording = useCallback(() => {
        const recorder = studentAudioRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            try {
                recorder.stop();
            } catch (error) {
                console.log('[StudentVideoStream] Student audio recorder stop ignored:', error.message);
            }
        }
    }, []);

    const startStudentAudioRecording = useCallback((stream) => {
        const audioTracks = stream?.getAudioTracks?.().filter(track => track.readyState === 'live') || [];
        if (!audioTracks.length || typeof MediaRecorder === 'undefined') return;

        const currentRecorder = studentAudioRecorderRef.current;
        const currentTrackId = currentRecorder?.stream?.getAudioTracks?.()[0]?.id;
        if (currentRecorder?.state !== 'inactive' && currentTrackId === audioTracks[0].id) return;

        stopStudentAudioRecording();
        const audioStream = new MediaStream(audioTracks);
        const mimeType = ['audio/webm;codecs=opus', 'audio/webm'].find(type => MediaRecorder.isTypeSupported(type));
        const recorder = mimeType ? new MediaRecorder(audioStream, { mimeType }) : new MediaRecorder(audioStream);
        studentAudioRecorderRef.current = recorder;

        recordingSocket.emit('server-tutor-cam', {
            source: 'student-local-audio',
            participant_user_id: user?.id,
        }, (filePath) => {
            let sendTail = Promise.resolve();
            let chunksSent = 0;

            recorder.ondataavailable = (event) => {
                if (!event.data || event.data.size === 0) return;
                chunksSent += 1;
                sendTail = sendTail.then(async () => {
                    const data = await blobToBase64(event.data);
                    recordingSocket.emit('cam_recording_data', JSON.stringify({ data, file_path: filePath }));
                });
            };
            recorder.onstop = async () => {
                await sendTail.catch(error => {
                    console.log('[StudentVideoStream] Student audio chunk flush failed:', error.message);
                });
                if (chunksSent > 0) {
                    recordingSocket.emit('cam_recording_data', JSON.stringify({
                        x: 'close recording',
                        width: 'audio',
                        file_path: filePath,
                    }));
                }
                if (studentAudioRecorderRef.current === recorder) studentAudioRecorderRef.current = null;
            };
            recorder.onerror = (event) => {
                console.log('[StudentVideoStream] Student audio recorder failed:', event.error?.message || event.type);
            };
            recorder.start(500);
        });
    }, [stopStudentAudioRecording, user?.id]);

    const destroyDirectTutorMeetingPeer = useCallback(() => {
        const current = directTutorMeetingPeerRef.current;
        if (current?.retryTimer) clearTimeout(current.retryTimer);
        if (current?.peer && !current.peer.destroyed) {
            try {
                current.peer.destroy();
            } catch (error) {
                console.log("[StudentVideoStream] Direct tutor meeting peer destroy error:", error.message);
            }
        }
        directTutorMeetingPeerRef.current = null;
    }, []);

    const createDirectTutorMeetingPeer = useCallback((tutor, stream, attendee) => {
        if (!tutor?.socket_id || !stream?.getTracks?.().length) return;

        const startAttempt = (attempt) => {
            destroyDirectTutorMeetingPeer();
            const peer = new Peer({
                initiator: true,
                trickle: true,
                stream,
                config: getIceConfig(),
            });
            peer.on("signal", (signal) => {
                socket.emit("student-direct-media-signal", {
                    receiverSocketId: tutor.socket_id,
                    senderUserId: attendee?.user_id,
                    senderAttendee: attendee ? {
                        socket_id: socket.id,
                        user_id: attendee.user_id,
                        user_name: attendee.user_name,
                        user_type: attendee.user_type,
                    } : null,
                    signal,
                });
            });
            peer.on("connect", () => {
                const current = directTutorMeetingPeerRef.current;
                if (current?.peer === peer && current.retryTimer) clearTimeout(current.retryTimer);
                console.log("[StudentVideoStream] Direct meeting media connected to tutor:", tutor.socket_id);
            });
            peer.on("error", (error) => {
                console.log("[StudentVideoStream] Direct meeting media peer error:", error.message);
            });
            const retryTimer = setTimeout(() => {
                const current = directTutorMeetingPeerRef.current;
                const connectionState = peer._pc?.connectionState;
                if (current?.peer === peer && connectionState !== 'connected' && attempt < 2) {
                    console.log("[StudentVideoStream] Retrying direct tutor meeting media:", attempt + 2);
                    startAttempt(attempt + 1);
                }
            }, 5000);
            directTutorMeetingPeerRef.current = { peer, tutorSocketId: tutor.socket_id, retryTimer };
        };

        startAttempt(0);
    }, [destroyDirectTutorMeetingPeer]);

    const requestTutorVideoStream = useCallback((reason = 'ready') => {
        cancelVideoReadinessRef.current?.();
        cancelVideoReadinessRef.current = emitReadinessWithRetry(
            socket,
            'student-ready-for-video-stream',
            {
                reason,
                room_id: String(socket.auth?.room_id || ''),
                readiness_version: 2,
            }
        );
    }, []);

    const clearDirectTutorAudio = useCallback(() => {
        const peer = directTutorAudioPeerRef.current;
        if (peer && !peer.destroyed) {
            try {
                peer.destroy();
            } catch (error) {
                console.log("[StudentVideoStream] Direct tutor audio destroy error:", error.message);
            }
        }
        directTutorAudioPeerRef.current = null;

        if (directTutorAudioRef.current) {
            directTutorAudioRef.current.srcObject = null;
        }

        if (directTutorMediaStreamRef.current && directTutorMediaStreamRef.current !== directTutorAudioStreamRef.current) {
            directTutorMediaStreamRef.current.getTracks().forEach((track) => track.stop());
        }
        directTutorMediaStreamRef.current = null;

        if (directTutorAudioStreamRef.current) {
            directTutorAudioStreamRef.current.getTracks().forEach((track) => track.stop());
        }
        directTutorAudioStreamRef.current = null;
        setDirectTutorAudioStream(null);
    }, []);

    useEffect(() => {
        directTutorAudioStreamRef.current = directTutorAudioStream;
    }, [directTutorAudioStream]);

    const playTutorAudio = useCallback(async () => {
        const audioElement = tutorAudioRef.current;
        if (!audioElement || !tutorStream || !tutorStream.getAudioTracks().length) return;

        if (audioElement.srcObject !== tutorStream) {
            audioElement.srcObject = tutorStream;
        }
        audioElement.muted = false;
        audioElement.volume = 1;

        try {
            await audioElement.play();
            setAudioPlaybackBlocked(false);
            console.log("[StudentVideoStream] Audio playback started successfully");
        } catch (error) {
            setAudioPlaybackBlocked(true);
            console.log("[StudentVideoStream] Audio play error:", error.message);
        }
    }, [tutorStream]);

    useEffect(() => {
        playTutorAudio();
    }, [playTutorAudio]);

    const playDirectTutorAudio = useCallback(async () => {
        const audioElement = directTutorAudioRef.current;
        if (!audioElement || !directTutorAudioStream || !directTutorAudioStream.getAudioTracks().length) return;

        if (audioElement.srcObject !== directTutorAudioStream) {
            audioElement.srcObject = directTutorAudioStream;
        }
        audioElement.muted = false;
        audioElement.volume = 1;

        try {
            await audioElement.play();
            setAudioPlaybackBlocked(false);
            console.log("[StudentVideoStream] Direct tutor audio playback started successfully");
        } catch (error) {
            setAudioPlaybackBlocked(true);
            console.log("[StudentVideoStream] Direct tutor audio play error:", error.message);
        }
    }, [directTutorAudioStream]);

    useEffect(() => {
        playDirectTutorAudio();
    }, [playDirectTutorAudio]);

    const playAvailableTutorAudio = useCallback(() => {
        playTutorAudio();
        playDirectTutorAudio();
    }, [playTutorAudio, playDirectTutorAudio]);

    useEffect(() => {
        requestTutorVideoStream('mount');

        const handleConnect = () => requestTutorVideoStream('connect');
        const handleOrientationChange = () => {
            setTimeout(() => requestTutorVideoStream('orientation-change'), 250);
        };

        socket.on('connect', handleConnect);
        window.addEventListener('orientationchange', handleOrientationChange);
        window.visualViewport?.addEventListener('resize', handleOrientationChange);

        return () => {
            cancelVideoReadinessRef.current?.();
            socket.off('connect', handleConnect);
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.visualViewport?.removeEventListener('resize', handleOrientationChange);
        };
    }, [requestTutorVideoStream]);

    useEffect(() => {
        if (!tutorCamEnabled || !tutorStream?.getVideoTracks?.().length) return;

        let attempts = 0;
        const timer = setInterval(() => {
            const videoElement = studentVideoRef.current;
            const videoTrack = tutorStream.getVideoTracks()[0];
            const hasRenderableVideo = Boolean(
                videoElement &&
                videoElement.videoWidth > 0 &&
                videoElement.videoHeight > 0 &&
                videoTrack &&
                videoTrack.readyState === 'live' &&
                !videoTrack.muted
            );

            if (hasRenderableVideo) {
                clearInterval(timer);
                return;
            }

            attempts += 1;
            if (attempts > 6) {
                clearInterval(timer);
                return;
            }

            console.log("[StudentVideoStream] Camera video stalled, requesting a fresh stream:", {
                attempt: attempts,
                videoWidth: videoElement?.videoWidth,
                videoHeight: videoElement?.videoHeight,
                trackMuted: videoTrack?.muted,
                trackReadyState: videoTrack?.readyState,
            });
            requestTutorVideoStream(`camera-stalled-${attempts}`);
        }, 4000);

        return () => clearInterval(timer);
    }, [requestTutorVideoStream, tutorCamEnabled, tutorStream]);

    useEffect(() => {
        // socket.emit("student-connected", {})
        socket.on(SOCKET_EVENTS.LEGACY_TUTOR_SIGNAL, (data) => {
            if (data?.room_id && String(data.room_id) !== String(socket.auth?.room_id || '')) return;
            console.log("[StudentVideoStream] Received totur-signal:", {
                signalType: data?.signal?.type,
                tutorId: data?.tutor_id,
                hasSignal: !!data?.signal,
            });
            
            if (data.signal.type !== 'offer') {
                // This is an ICE candidate or answer - forward to existing peer
                if (studentPeerRef.current && !studentPeerRef.current.destroyed) {
                    safeSignalPeer(studentPeerRef.current, data.signal, "StudentVideoStream tutor-answer");
                } else {
                    console.log("[StudentVideoStream] WARNING: No peer to signal for non-offer signal");
                }
            } else {
                // This is an offer - ALWAYS create new peer for renegotiation
                // The tutor may have switched stream type (video->audio or audio->video)
                console.log("[StudentVideoStream] Received new offer, creating new peer");
                
                // Destroy any existing peer before creating new one
                if (studentPeerRef.current) {
                    console.log("[StudentVideoStream] Destroying existing peer for renegotiation");
                    try {
                        studentPeerRef.current.destroy();
                    } catch (e) {
                        console.log("[StudentVideoStream] Error destroying peer:", e.message);
                    }
                    studentPeerRef.current = null;
                }
                
                isReceivingCameraStream.current = true;
                
                const iceConfig = getIceConfig();
                const newPeer = new Peer({
                    initiator: false,
                    trickle: true,
                    config: iceConfig
                });
                safeSignalPeer(newPeer, data.signal, "StudentVideoStream tutor-offer")
                newPeer.on("stream", (stream) => {
                    const videoTracks = stream.getVideoTracks();
                    const audioTracks = stream.getAudioTracks();
                    
                    console.log("[StudentVideoStream] *** STREAM RECEIVED FROM TUTOR ***");
                    console.log("[StudentVideoStream] Stream details:", {
                        streamId: stream.id,
                        streamActive: stream.active,
                        videoTracks: videoTracks.length,
                        audioTracks: audioTracks.length,
                    });
                    
                    if (videoTracks.length > 0) {
                        const vt = videoTracks[0];
                        
                        // Get track settings to see actual dimensions
                        const settings = vt.getSettings();
                        console.log("[StudentVideoStream] Video track details:", {
                            id: vt.id,
                            label: vt.label,
                            kind: vt.kind,
                            enabled: vt.enabled,
                            muted: vt.muted,
                            readyState: vt.readyState,
                            contentHint: vt.contentHint,
                        });
                        console.log("[StudentVideoStream] Video track settings:", {
                            width: settings.width,
                            height: settings.height,
                            frameRate: settings.frameRate,
                            aspectRatio: settings.aspectRatio,
                        });
                        
                        if (!settings.width || !settings.height) {
                            console.warn("[StudentVideoStream] WARNING: Video track has no dimensions!");
                        }
                        
                        // Add track event listeners
                        vt.onended = () => console.log("[StudentVideoStream] Video track ended!");
                        vt.onmute = () => console.log("[StudentVideoStream] Video track muted!");
                        vt.onunmute = () => console.log("[StudentVideoStream] Video track unmuted!");
                        
                        // Force enable the track
                        if (!vt.enabled) {
                            console.log("[StudentVideoStream] Enabling disabled video track");
                            vt.enabled = true;
                        }
                        
                        setTutorCamEnabled(true);
                    } else { 
                        console.log("[StudentVideoStream] Audio-only stream received (no video tracks)");
                        setTutorCamEnabled(false);
                    }
                    
                    if (audioTracks.length > 0) {
                        const at = audioTracks[0];
                        console.log("[StudentVideoStream] Audio track details:", {
                            id: at.id,
                            label: at.label,
                            enabled: at.enabled,
                            muted: at.muted,
                            readyState: at.readyState,
                        });
                        
                        // Force enable audio
                        if (!at.enabled) {
                            console.log("[StudentVideoStream] Enabling disabled audio track");
                            at.enabled = true;
                        }
                    } else {
                        console.warn("[StudentVideoStream] WARNING: No audio tracks in stream!");
                    }
                    
                    const directMediaStream = directTutorMediaStreamRef.current;
                    const hasDirectVideo = directMediaStream?.getVideoTracks?.().some(track => (
                        track.readyState === 'live' && !track.muted
                    ));

                    if (hasDirectVideo && videoTracks.length > 0) {
                        console.log("[StudentVideoStream] Keeping direct tutor media stream instead of stale relay stream");
                        return;
                    }

                    setTutorStream(stream);
                })

                newPeer.on("signal", (signal) => {
                    console.log("[StudentVideoStream] Sending student-signal to tutor:", data.tutor_id, "type:", signal.type);
                    socket.emit(SOCKET_EVENTS.STUDENT_SIGNAL, { signal, tutor_id: data.tutor_id });

                });
                newPeer.on('error', (err) => { 
                    console.log("[StudentVideoStream] Peer error:", err.message);
                    isReceivingCameraStream.current = false;
                })
                newPeer.on('connect', () => { 
                    console.log("[StudentVideoStream] Peer connected successfully");
                })
                newPeer.on('close', () => {
                    console.log("[StudentVideoStream] Peer closed");
                    isReceivingCameraStream.current = false;
                })
                studentPeerRef.current = newPeer;
            }
        })
        socket.on("tutor-direct-audio-signal", (data = {}) => {
            if (!data.signal) return;

            if (data.signal.type !== 'offer') {
                if (directTutorAudioPeerRef.current && !directTutorAudioPeerRef.current.destroyed) {
                    safeSignalPeer(directTutorAudioPeerRef.current, data.signal, "StudentVideoStream direct-audio-non-offer");
                }
                return;
            }

            if (directTutorAudioPeerRef.current) {
                try {
                    directTutorAudioPeerRef.current.destroy();
                } catch (error) {
                    console.log("[StudentVideoStream] Direct tutor audio replace error:", error.message);
                }
                directTutorAudioPeerRef.current = null;
            }

            const peer = new Peer({
                initiator: false,
                trickle: true,
                config: getIceConfig()
            });

            const acceptDirectTutorAudioStream = (stream) => {
                const audioTracks = stream?.getAudioTracks?.() || [];
                const videoTracks = stream?.getVideoTracks?.() || [];
                console.log("[StudentVideoStream] Direct tutor media stream received:", {
                    streamId: stream?.id,
                    active: stream?.active,
                    videoTracks: videoTracks.length,
                    audioTracks: audioTracks.length,
                    firstTrack: audioTracks[0] ? {
                        enabled: audioTracks[0].enabled,
                        muted: audioTracks[0].muted,
                        readyState: audioTracks[0].readyState,
                        label: audioTracks[0].label,
                    } : null,
                });

                if (!audioTracks.length && !videoTracks.length) return;

                audioTracks.forEach((track) => {
                    if (!track.enabled) track.enabled = true;
                });

                videoTracks.forEach((track) => {
                    if (!track.enabled) track.enabled = true;
                });

                if (directTutorMediaStreamRef.current && directTutorMediaStreamRef.current !== stream) {
                    directTutorMediaStreamRef.current.getTracks().forEach((track) => track.stop());
                }
                directTutorMediaStreamRef.current = stream;

                if (videoTracks.length) {
                    setTutorCamEnabled(true);
                    setTutorStream(stream);
                }

                if (audioTracks.length) {
                    directTutorAudioStreamRef.current = stream;
                    setDirectTutorAudioStream(stream);
                }
            };

            peer.on("signal", (signal) => {
                socket.emit("student-direct-audio-signal", {
                    tutorSocketId: data.tutorSocketId,
                    signal
                });
            });
            peer.on("stream", acceptDirectTutorAudioStream);
            peer.on("track", (track, stream) => {
                if (track.kind === 'audio' || track.kind === 'video') {
                    acceptDirectTutorAudioStream(stream || new MediaStream([track]));
                }
            });
            peer.on("connect", () => {
                console.log("[StudentVideoStream] Direct tutor audio peer connected");
            });
            peer.on("close", () => {
                console.log("[StudentVideoStream] Direct tutor audio peer closed");
                if (directTutorAudioRef.current) directTutorAudioRef.current.srcObject = null;
                directTutorMediaStreamRef.current = null;
                directTutorAudioStreamRef.current = null;
                setDirectTutorAudioStream(null);
            });
            peer.on("error", (error) => {
                console.log("[StudentVideoStream] Direct tutor audio peer error:", error.message);
            });

            directTutorAudioPeerRef.current = peer;
            safeSignalPeer(peer, data.signal, "StudentVideoStream direct-audio-offer");
        })
        socket.on("student-direct-media-signal", (data) => {
            const current = directTutorMeetingPeerRef.current;
            if (
                !current?.peer ||
                current.peer.destroyed ||
                current.tutorSocketId !== data?.senderSocketId
            ) return;
            safeSignalPeer(current.peer, data.signal, "StudentVideoStream direct-meeting-answer");
        })
        socket.on("student-disconnect", (socket_id) => {
            const disconnectdSenderStudent = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === socket_id);
            if (disconnectdSenderStudent) {
                disconnectdSenderStudent?.peer.destroy();
                senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== socket_id);
            }
        })

        socket.on("user-disconnected-meeting", (student) => {
            if (student) {
                attendeesRef.current = attendeesRef.current.filter(a => a.socket_id !== student.socket_id)
                setAttendees(attendeesRef.current)
            }

        })

        socket.on("close-mic", (socket_id) => {
            if (String(socket_id) === String(socket.id)) stopStudentAudioRecording();
            if (studentsPeerRefs.current.length) studentsPeerRefs.current.map(p => p?.peer.destroy());
            studentsPeerRefs.current = [];
            attendeesRef.current.map(s => {
                if (s.socket_id === socket_id) {
                    if (s.stream) {
                        s.stream.getTracks().forEach((track) => {
                            track.stop();
                        });
                        s.stream = null;
                    }
                }
            })
            setAttendees(attendeesRef.current)

        })
        socket.on("close-student-mic", (socket_id) => {
            if (senderStudentsPeerRefs.current.length) {
                const disconnectdSenderStudent = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === socket_id);
                if (disconnectdSenderStudent) {
                    disconnectdSenderStudent?.peer.destroy();
                    senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== socket_id);
                }
            }
            attendeesRef.current.map(s => {
                if (s.socket_id === socket_id) {
                    if (s.stream) {
                        s.stream.getTracks().forEach((track) => {
                            track.stop();
                        });
                        s.stream = null;
                    }
                }
            })
            setAttendees(attendeesRef.current)
        })

        socket.on("student-stream-signal", (data) => {
            if (data.signal.type === 'offer') {
                const iceConfig = getIceConfig();
                const newPeer = new Peer({
                    initiator: false,
                    trickle: true,
                    config: iceConfig
                });
                safeSignalPeer(newPeer, data.signal, "StudentVideoStream attendee-offer")
                newPeer.on("stream", (stream) => {
                    attendeesRef.current = attendeesRef.current.map(a => {
                        if (
                            a.socket_id === data.sender_attendee_socket_id ||
                            (data.sender_attendee_user_id != null && String(a.user_id) === String(data.sender_attendee_user_id))
                        ) {
                            a.stream = stream
                        }
                        return a;
                    })
                })
                newPeer.on("signal", (signal) => {
                    socket.emit("student-receiver-stream-signal", { signal, sender_attendee_socket_id: data.sender_attendee_socket_id, sender_attendee_user_id: data.sender_attendee_user_id, sender_server_handler_socket_id: data.sender_server_handler_socket_id, peer_socket_id: socket.id });
                });
                newPeer.on("connect", () => {
                    if (attendeesRef.current) setAttendees([...attendeesRef.current])
                })
                newPeer.on('error', (err) => { console.log("the peer error is ", err) })
                const timeStamp = Date.now();
                if (senderStudentsPeerRefs.current.length) {
                    senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== data.sender_attendee_socket_id)
                    senderStudentsPeerRefs.current.push({ peer: newPeer, sender_student_socket_id: data.sender_attendee_socket_id, timeStamp });

                } else {
                    senderStudentsPeerRefs.current.push({ peer: newPeer, sender_student_socket_id: data.sender_attendee_socket_id });
                }
            } else {
                const studentSocketId = data.sender_attendee_socket_id;
                if (senderStudentsPeerRefs.current.length) {
                    const studentPeer = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === studentSocketId)
                    if (studentPeer) {
                        safeSignalPeer(studentPeer?.peer, data.signal, "StudentVideoStream attendee-answer");
                    }
                }
            }
        })

        socket.on("server-receiver-stream-to-student-signal", (data) => {
            const signal = data.data;
            if (serverHandlerPeer.current) {
                safeSignalPeer(serverHandlerPeer.current, signal, "StudentVideoStream server-handler");
            }
        })
        recordingSocket.on("end-session-student", () => {
            console.log("[StudentVideoStream] Received end-session-student on recordingSocket");
            stopStudentAudioRecording();
            if (studentsPeerRefs.current) studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (senderStudentsPeerRefs.current) senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (serverHandlerPeer.current) { serverHandlerPeer.current.destroy(); serverHandlerPeer.current = null }
            destroyDirectTutorMeetingPeer();
            clearDirectTutorAudio();

            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                    a.stream = false;
                }
            })
            studentsPeerRefs.current = []
            senderStudentsPeerRefs.current = [];
            attendeesRef.current = []
            navigate("/dashboard")
        })
        // Also listen on main socket for end-session-student (fallback)
        socket.on("end-session-student", () => {
            console.log("[StudentVideoStream] Received end-session-student on main socket");
            stopStudentAudioRecording();
            if (studentsPeerRefs.current) studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (senderStudentsPeerRefs.current) senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (serverHandlerPeer.current) { serverHandlerPeer.current.destroy(); serverHandlerPeer.current = null }
            destroyDirectTutorMeetingPeer();
            clearDirectTutorAudio();

            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                    a.stream = false;
                }
            })
            studentsPeerRefs.current = []
            senderStudentsPeerRefs.current = [];
            attendeesRef.current = []
            navigate("/dashboard")
        })
        socket.on("stop-video-recording-received", () => {
            if (studentPeerRef?.current !== null) {
                studentPeerRef.current.destroy();
                studentPeerRef.current = null;
            }
            // Clear the video ref AFTER stopping the stream
            if (studentVideoRef.current) {
                studentVideoRef.current.srcObject = null;
            }
            clearDirectTutorAudio();
            setTutorStream(null)
        })

        socket.on("tutor-cam-enabled", (condition) => {
            setTutorCamEnabled(condition)
        })
        socket.on("student-connected-meeting", student => {
            const foundUser = attendeesRef.current.find(a => a.user_id === student.user_id)
            if (foundUser) {
                attendeesRef.current = [...attendeesRef.current.filter(a => a.user_id !== student.user_id), student]
            } else {
                attendeesRef.current = [...attendeesRef.current, student]
            }
            setAttendees(attendeesRef.current)
        })
        socket.on("meeting-state-sent", (data) => {
            const dataParsed = JSON.parse(data);
            setMeetingStarted(dataParsed.state)
        })
        socket.on("meeting-end-session", () => {
            stopStudentAudioRecording();
            if (studentsPeerRefs.current) studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (senderStudentsPeerRefs.current) senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                }
            })
            attendeesRef.current = []
        })
        socket.on("connect_error", error => console.log(error))

        return () => {
            stopStudentAudioRecording();
            recordingSocket.off("end-session-student")
            socket.off("end-session-student");
            socket.off("meeting-state-sent");
            socket.off("meeting-end-session");
            socket.off("student-connected-meeting");
            socket.off("user-disconnected-meeting");
            socket.off("tutor-cam-enabled");
            socket.off("connect_error");
            socket.off("close-mic");
            socket.off("close-student-mic");
            socket.off("student-stream-signal")
            socket.off(SOCKET_EVENTS.LEGACY_TUTOR_SIGNAL);
            socket.off("tutor-direct-audio-signal");
            socket.off("student-direct-media-signal");
            socket.off("student-disconnect");
            socket.off('stop-video-recording-received')
            socket.off('start-recording-received')
            if (studentPeerRef?.current) {
                studentPeerRef.current.destroy();
                studentPeerRef.current = null;
            }
            // Clear the srcObject before nullifying the ref
            if (studentVideoRef.current) {
                studentVideoRef.current.srcObject = null;
            }
            if (tutorStream) {
                tutorStream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
            if (studentsPeerRefs.current) studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (senderStudentsPeerRefs.current) senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            if (serverHandlerPeer.current) { serverHandlerPeer.current.destroy(); serverHandlerPeer.current = null }
            destroyDirectTutorMeetingPeer();
            clearDirectTutorAudio();

            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                }
            })
            studentsPeerRefs.current = []
            senderStudentsPeerRefs.current = [];
            attendeesRef.current = []
            setTutorStream(null)
        }

        // Socket listeners are owned by this mounted classroom instance. Rebinding
        // them on stream/state changes tears down otherwise healthy media tracks.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const createServerHandlerPeer = (serverHandler, stream, attendee) => {
        console.log("[StudentVideoStream] createServerHandlerPeer called");
        console.log("[StudentVideoStream] Stream tracks:", stream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState })));
        console.log("[StudentVideoStream] Server handler:", serverHandler.socket_id);
        console.log("[StudentVideoStream] Attendee:", attendee?.socket_id, attendee?.user_id);
        
        // Destroy existing peer if any
        if (serverHandlerPeer.current) {
            console.log("[StudentVideoStream] Destroying existing server handler peer");
            try {
                serverHandlerPeer.current.destroy();
            } catch (e) {
                console.log("[StudentVideoStream] Error destroying peer:", e.message);
            }
            serverHandlerPeer.current = null;
        }
        
        const iceConfig = getIceConfig();
        const newPeer = new Peer({
            initiator: true,
            trickle: true,
            stream,
            config: iceConfig
        });
        
        newPeer.on("signal", (data) => {
            console.log("[StudentVideoStream] Peer signal, type:", data.type);
            socket.emit("student-stream-signal-to-server-handler", { signal: data, handlerSocketId: serverHandler.socket_id, peerSocketId: socket.id, attendee });
        });
        newPeer.on("connect", () => {
            console.log("[StudentVideoStream] *** Peer connected to server-handler ***");
        });
        newPeer.on("error", (err) => {
            console.log("[StudentVideoStream] Peer error:", err.message);
        });
        newPeer.on("close", () => {
            console.log("[StudentVideoStream] Peer closed");
        });
        serverHandlerPeer.current = newPeer;
    }
    useEffect(() => {
        if (attendees.length && !meetingStarted) {
            attendees.map(s_a => {
                if (s_a.user_id !== user.id) {
                    const socket_id = s_a.socket_id;
                    const foundAudioEl = document.getElementById(socket_id);
                    if (foundAudioEl) {
                        foundAudioEl.srcObject = s_a.stream
                    } else {
                        var audioEl = document.createElement('audio');
                        audioEl.setAttribute("id", s_a.socket_id)
                        audioEl.controls = false;
                        audioEl.autoplay = true;
                        audioEl.srcObject = s_a.stream;
                        document.body.appendChild(audioEl)

                    }

                }
            })
        }
    }, [attendees, meetingStarted])

    useEffect(() => {
        // Guard against null refs
        if (!studentVideoRef.current || !tutorStream) {
            return;
        }
        
        const videoElement = studentVideoRef.current;
        
        console.log("[StudentVideoStream] Setting video srcObject:", {
            videoRefExists: !!videoElement,
            streamActive: tutorStream.active,
            streamId: tutorStream.id,
            videoTracks: tutorStream.getVideoTracks().length,
            audioTracks: tutorStream.getAudioTracks().length,
            tutorCamEnabled,
            meetingStarted
        });
        
        // Check if video tracks are live
        const videoTrack = tutorStream.getVideoTracks()[0];
        if (videoTrack) {
            console.log("[StudentVideoStream] Video track state:", {
                id: videoTrack.id,
                label: videoTrack.label,
                enabled: videoTrack.enabled,
                muted: videoTrack.muted,
                readyState: videoTrack.readyState,
                kind: videoTrack.kind
            });
            
            // Force enable the track if it's disabled
            if (!videoTrack.enabled) {
                console.log("[StudentVideoStream] Enabling disabled video track");
                videoTrack.enabled = true;
            }
        } else {
            console.warn("[StudentVideoStream] WARNING: No video track in stream!");
        }
        
        // Set the stream. Keep the video element muted so browser autoplay
        // policies do not block the first video frames; audio is played by the
        // dedicated audio element below.
        videoElement.srcObject = tutorStream;
        videoElement.defaultMuted = true;
        videoElement.muted = true;
        
        // Add event listeners with null checks
        const handleLoadedMetadata = () => {
            if (videoElement) {
                console.log("[StudentVideoStream] Video metadata loaded, dimensions:", 
                    videoElement.videoWidth, "x", videoElement.videoHeight);
                
                // If dimensions are 0, there might be an issue
                if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
                    console.warn("[StudentVideoStream] WARNING: Video dimensions are 0x0!");
                }
            }
        };
        
        const handlePlay = () => {
            console.log("[StudentVideoStream] Video started playing");
            if (videoElement) {
                console.log("[StudentVideoStream] Playing video dimensions:", 
                    videoElement.videoWidth, "x", videoElement.videoHeight);
            }
        };
        
        const handleError = (e) => {
            console.error("[StudentVideoStream] Video element error:", e);
        };
        
        const handleCanPlay = () => {
            console.log("[StudentVideoStream] Video can play, dimensions:", 
                videoElement?.videoWidth, "x", videoElement?.videoHeight);
        };
        
        videoElement.onloadedmetadata = handleLoadedMetadata;
        videoElement.onplay = handlePlay;
        videoElement.onerror = handleError;
        videoElement.oncanplay = handleCanPlay;
        
        // Try to play the video explicitly
        const playVideo = async () => {
            if (!videoElement) return;
            
            try {
                await videoElement.play();
                console.log("[StudentVideoStream] Video play started (muted)");
            } catch (err) {
                const message = err?.message || '';
                if (err?.name === 'AbortError' || message.includes('interrupted by a new load request')) {
                    console.log("[StudentVideoStream] Video play interrupted by stream replacement");
                    return;
                }
                console.error("[StudentVideoStream] Video play error:", message);
            }
        };
        
        playVideo();
        
        // Cleanup
        return () => {
            if (videoElement) {
                videoElement.onloadedmetadata = null;
                videoElement.onplay = null;
                videoElement.onerror = null;
                videoElement.oncanplay = null;
            }
        };

    }, [tutorStream, tutorCamEnabled, meetingStarted])

    useEffect(() => {
        if (meetingStarted && attendees.length && user) {
            console.log("the user reading to receive stream", user.id)
            socket.emit("user-ready-to-receive-stream", user.id)
        }
        studentsPeerRefs.current.forEach(p => { if (p.peer) p.peer.destroy() });
        senderStudentsPeerRefs.current.forEach(p => { if (p.peer) p.peer.destroy() });
        socket.emit("get-connected-students-include-sender", students => {
            const localStreams = new Map(attendeesRef.current
                .filter(attendee => attendee.stream)
                .map(attendee => [attendee.user_id, attendee.stream]));
            const hydratedStudents = students.map(student => ({
                ...student,
                stream: student.stream || localStreams.get(student.user_id),
            }));
            attendeesRef.current = hydratedStudents;
            setAttendees(hydratedStudents);
        })
    }, [meetingStarted, user])
    const handleStreamStudentAudio = (attendee) => {
        if (!attendee) {
            attendee = attendeesRef.current.find(a => a.user_id === user.id)
            socket.emit("student-mic-connected", { student_socket_id: attendee.socket_id })
        }
        if (attendee.stream) {
            if (attendee.camEnabled) {
                attendeesRef.current = attendeesRef.current.map(a => {
                    if (a.user_id === attendee.user_id) {
                        const micEnabled = !attendee.micEnabled;
                        a.stream.getAudioTracks().forEach(track => { track.enabled = micEnabled; });
                        return { ...a, micEnabled };
                    }
                    return a;
                })
                setAttendees([...attendeesRef.current])
            } else {
                attendee.stream.getTracks().forEach((track) => {
                    track.stop();
                });
                stopStudentAudioRecording();
                destroyDirectTutorMeetingPeer();
                socket.emit("kill-attendee-stream", attendee)
                attendeesRef.current = attendeesRef.current.map(a => {
                    if (a.user_id === attendee.user_id) {
                        return { ...a, stream: null, micEnabled: false };
                    }
                    return a;
                })
                if (studentsPeerRefs.current.length) studentsPeerRefs.current.map(p => p?.peer.destroy());
                studentsPeerRefs.current = [];
                setAttendees([...attendeesRef.current])
            }

        } else {
            const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
            const constraints = {
                video: false, // Fallback to true if any of the video constraints are not supported
                audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                    sampleRate: 22050,
                    sampleSize: 16,
                    channelCount: 1,
                } : true // Fallback to true if any of the audio constraints are not supported
            };
            navigator.mediaDevices.getUserMedia(constraints).then(str => {
                attendeesRef.current = attendeesRef.current.map(a => {
                    if (a.user_id === attendee.user_id) {
                        return { ...a, stream: str, micEnabled: true, camEnabled: false };
                    }
                    return a;
                })
                setAttendees([...attendeesRef.current])
                handleStreamToServer(attendee, str)

            }).catch(error => console.log(error))
        }
    }
    const handleStreamStudentCam = (attendee) => {
        if (attendee.stream) {
            if (attendee.camEnabled) {
                if (studentsPeerRefs.current.length) studentsPeerRefs.current.map(p => p?.peer.destroy());
                studentsPeerRefs.current = [];
                attendee.stream.getTracks().forEach((track) => {
                    track.stop();
                });
                const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                const constraints = {
                    video: false, // Fallback to true if any of the video constraints are not supported
                    audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                        sampleRate: 22050,
                        sampleSize: 16,
                        channelCount: 1,
                    } : true // Fallback to true if any of the audio constraints are not supported
                };
                navigator.mediaDevices.getUserMedia(constraints).then(str => {
                    attendeesRef.current = attendeesRef.current.map(a => {
                        if (a.user_id === attendee.user_id) {
                            return { ...a, stream: str, micEnabled: true, camEnabled: false };
                        }
                        return a;
                    })
                    setAttendees([...attendeesRef.current])
                    handleStreamToServer(attendee, str)
                }).catch(error => console.log(error))

            } else {
                if (studentsPeerRefs.current.length) studentsPeerRefs.current.map(p => p?.peer.destroy());
                studentsPeerRefs.current = [];
                if (attendee.stream) {
                    attendee.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                }
                const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                const constraints = {
                    video: supportedConstraints.width && supportedConstraints.frameRate && supportedConstraints.aspectRatio ? {
                        width: { max: 360 },
                        frameRate: { max: 10 },
                        aspectRatio: { exact: 1.55 }
                    } : true, // Fallback to true if any of the video constraints are not supported
                    audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                        sampleRate: 22050,
                        sampleSize: 16,
                        channelCount: 1,
                    } : true // Fallback to true if any of the audio constraints are not supported
                };
                navigator.mediaDevices.getUserMedia(constraints).then(str => {
                    attendeesRef.current = attendeesRef.current.map(a => {
                        if (a.user_id === attendee.user_id) {
                            return { ...a, stream: str, micEnabled: true, camEnabled: true };
                        }
                        return a;
                    })
                    setAttendees([...attendeesRef.current])
                    handleStreamToServer(attendee, str)
                }).catch(error => console.log(error))
            }

        } else {
            const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
            const constraints = {
                video: supportedConstraints.width && supportedConstraints.frameRate && supportedConstraints.aspectRatio ? {
                    width: { max: 360 },
                    frameRate: { max: 10 },
                    aspectRatio: { exact: 1.55 }
                } : true, // Fallback to true if any of the video constraints are not supported
                audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                    sampleRate: 22050,
                    sampleSize: 16,
                    channelCount: 1,
                } : true // Fallback to true if any of the audio constraints are not supported
            };
            navigator.mediaDevices.getUserMedia(constraints).then(str => {
                attendeesRef.current = attendeesRef.current.map(a => {
                    if (a.user_id === attendee.user_id) {
                        return { ...a, stream: str, micEnabled: true, camEnabled: true };
                    }
                    return a;
                })
                setAttendees([...attendeesRef.current])
                handleStreamToServer(attendee, str)


            }).catch(error => console.log(error))
        }
    }
    const handleStreamToServer = useCallback((attendee, str) => {
        console.log("[StudentVideoStream] handleStreamToServer called, stream tracks:", str.getTracks().length);
        startStudentAudioRecording(str);
        socket.emit("get-connected-students-tutors", connectedUsers => {
            const tutors = (connectedUsers || []).filter(a => a.user_type !== 0 && a.socket_id !== socket.id);
            const tutor = tutors.find(a => !a.isRecording) || tutors[0];
            if (tutor) {
                createDirectTutorMeetingPeer(tutor, str, attendee);
            } else {
                console.log("[StudentVideoStream] No tutor available for direct meeting media");
            }
        });
        socket.emit("get-server-handler", serverHandler => {
            if (serverHandler) {
                console.log("[StudentVideoStream] Got server handler:", serverHandler.socket_id);
                createServerHandlerPeer(serverHandler, str, attendee);
            } else {
                console.log("[StudentVideoStream] No server handler available!");
            }
        })
    }, [createDirectTutorMeetingPeer, startStudentAudioRecording])

    // Keep the full meeting consumer mounted; MeetingLayout renders through a
    // portal while the tutor preview remains inside the draggable circle.
    if (isMobileOverlay) {
        return (
            <>
                <StudentMeeting
                    attendees={attendees}
                    tutorStream={tutorStream}
                    meetingStarted={meetingStarted}
                    handleStreamStudentAudio={handleStreamStudentAudio}
                    handleStreamStudentCam={handleStreamStudentCam}
                />
                <Dialog
                    open={askedForMic}
                    aria-labelledby="student-mic-request-title"
                    PaperProps={{ sx: { background: 'transparent', boxShadow: 'none' } }}
                >
                        <AskedForMicWrapper>
                            <Typography id="student-mic-request-title" variant='h4'>{t('ask_for_mic')}</Typography>
                            <ButtonsRow>
                                <Button 
                                    onClick={() => { 
                                        socket.emit('student-aplogized-mic', user.id); 
                                        onMicRequestHandled?.();
                                    }} 
                                    variant="contained" 
                                    sx={{ backgroundColor: theme.palette.danger?.main || '#ef4444' }}
                                >
                                    {t("apologize")}
                                </Button>
                                <Button 
                                    onClick={() => { 
                                        console.log("[StudentVideoStream] Mobile: Accept mic clicked");
                                        handleStreamStudentAudio(null); 
                                        onMicRequestHandled?.();
                                    }} 
                                    variant="contained"
                                >
                                    {t("accept")}
                                </Button>
                            </ButtonsRow>
                        </AskedForMicWrapper>
                </Dialog>
                
                <Box sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    bgcolor: '#0a0a14',
                }}>
                    <video 
                        ref={studentVideoRef}
                        style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }} 
                        autoPlay
                        playsInline
                        muted
                    />
                    {!tutorCamEnabled && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(26, 26, 46, 0.9)',
                            color: '#fff',
                            fontSize: 10,
                            textAlign: 'center',
                        }}>
                            Camera Off
                        </Box>
                    )}
                </Box>
                
                {/* Audio element for mobile */}
                <audio
                    ref={tutorAudioRef}
                    autoPlay
                    playsInline
                    style={{ display: 'none' }}
                    data-testid="tutor-audio"
                />
                <audio
                    ref={directTutorAudioRef}
                    autoPlay
                    playsInline
                    style={{ display: 'none' }}
                    data-testid="direct-tutor-audio"
                />
                {audioPlaybackBlocked && hasTutorAudio && (
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 3000,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            background: 'rgba(15, 23, 42, 0.94)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: '#fff', whiteSpace: 'nowrap' }}>
                            {t('audioBlocked') || 'Audio is paused'}
                        </Typography>
                        <Button size="small" variant="contained" onClick={playAvailableTutorAudio}>
                            {t('enableAudio') || 'Enable audio'}
                        </Button>
                    </Box>
                )}
            </>
        );
    }

    return (
        <VideoBox showCamera={showCamera}>
            <StudentMeeting attendees={attendees} tutorStream={tutorStream} meetingStarted={meetingStarted} handleStreamStudentAudio={(attendee) => handleStreamStudentAudio(attendee)} handleStreamStudentCam={(attendee) => handleStreamStudentCam(attendee)} />
            <Dialog
                open={askedForMic}
                aria-labelledby="student-mic-request-title"
                PaperProps={{ sx: { background: 'transparent', boxShadow: 'none' } }}
            >
                <AskedForMicWrapper>
                    <Typography id="student-mic-request-title" variant='h4'>{t('ask_for_mic')}</Typography>
                    <ButtonsRow>
                        <Button onClick={() => { socket.emit('student-aplogized-mic', user.id); onMicRequestHandled?.(); }} variant="contained" sx={{ backgroundColor: theme.palette.danger.main }}>{t("apologize")}</Button>
                        <Button onClick={() => { handleStreamStudentAudio(null); onMicRequestHandled?.(); }} variant="contained">{t("accept")}</Button>
                    </ButtonsRow>
                </AskedForMicWrapper>
            </Dialog>
            {/* Camera Video Box - Modern glassmorphism styling */}
            <Box 
                ref={camerBoxRef}
                data-testid="camera-box"
                sx={{
                    display: showCamera ? 'block' : 'none',
                    background: 'linear-gradient(145deg, rgba(26, 26, 46, 0.9) 0%, rgba(15, 15, 26, 0.95) 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '16px',
                    width: '100%',
                    maxWidth: '100%',
                    aspectRatio: '16 / 10',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
                        transform: 'translateY(-2px)',
                    },
                    // Responsive
                    '@media (max-width: 768px)': {
                        width: '100%',
                        maxWidth: '300px',
                        height: 'auto',
                        aspectRatio: '16/9',
                        borderRadius: '12px',
                    },
                }}
            >
                {/* Live indicator */}
                <Box sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: tutorStream?.active ? 'rgba(239, 68, 68, 0.9)' : 'rgba(100, 100, 100, 0.9)',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    zIndex: 2,
                    backdropFilter: 'blur(4px)',
                }}>
                    <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        animation: tutorStream?.active ? 'pulse 1.5s infinite' : 'none',
                        '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                        },
                    }} />
                    <Typography sx={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        color: '#fff',
                        letterSpacing: '0.5px',
                    }}>
                        {tutorStream?.active ? 'LIVE' : 'OFFLINE'}
                    </Typography>
                </Box>
                
                <video 
                    id='live_stream_video' 
                    ref={studentVideoRef}
                    data-testid="camera-video"
                    style={{ 
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        backgroundColor: '#0a0a14',
                        display: 'block',
                        opacity: 1,
                    }} 
                    autoPlay
                    playsInline
                    muted
                />
                
                {/* Tutor name overlay */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    padding: '20px 12px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <Typography sx={{
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 500,
                    }}>
                        Teacher Camera
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        padding: '2px 8px',
                        borderRadius: '12px',
                    }}>
                        <Typography sx={{ 
                            fontSize: '10px', 
                            color: 'rgba(255,255,255,0.7)',
                        }}>
                            {tutorStream?.getVideoTracks().length || 0}v/{tutorStream?.getAudioTracks().length || 0}a
                        </Typography>
                    </Box>
                </Box>
            </Box>
            
            {/* Audio element for tutor stream - always render when we have audio tracks
                This handles both:
                1. Audio-only streams (no video)
                2. Video+audio streams (video element handles video, this handles audio)
            */}
            <audio
                ref={tutorAudioRef}
                autoPlay
                playsInline
                style={{ display: 'none', position: 'absolute' }}
                data-testid="tutor-audio"
            />
            <audio
                ref={directTutorAudioRef}
                autoPlay
                playsInline
                style={{ display: 'none', position: 'absolute' }}
                data-testid="direct-tutor-audio"
            />
            {audioPlaybackBlocked && hasTutorAudio && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 30,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        background: 'rgba(15, 23, 42, 0.92)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                    }}
                >
                    <Typography variant="caption" sx={{ color: '#fff', whiteSpace: 'nowrap' }}>
                        {t('audioBlocked') || 'Audio is paused'}
                    </Typography>
                    <Button size="small" variant="contained" onClick={playAvailableTutorAudio}>
                        {t('enableAudio') || 'Enable audio'}
                    </Button>
                </Box>
            )}
        </VideoBox>
    )
}


export default memo(StudentVideoStream)
